import { inject, Injectable } from "@angular/core";
import { AbstractControlOptions, AsyncValidatorFn, ValidatorFn } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { JsonUtils } from "@ramudden/core/utils";
import { ViewModelEnum } from "@ramudden/models/domain-data";
import { SigncoFormControl } from "@ramudden/models/form";
import { TimeAggregationType } from "@ramudden/models/pinned-data";
import { TimeUnit } from "chart.js";
import { SelectItem } from "primeng/api";
import { PrimeNG } from "primeng/config";
import { DatePicker } from "primeng/datepicker";
import { BehaviorSubject } from "rxjs";
import { LegendRow } from "../shared/components/legend/legend.component";
import { TwoLevelSelectOption } from "../shared/components/two-level-select/two-level-select.component";

export class CalendarSettings {
    constructor(
        public locale: string,
        public dateFormat: string,
        public monthFormat: string,
        public hourFormat: "12" | "24",
        public firstDayOfWeek: number,
    ) {
        const curYear = new Date().getFullYear();
        this.currentYearRange = `${curYear}:${curYear + 100}`;
    }

    currentYearRange: string;
}

class DateMeta {
    constructor(
        public day: number,
        public month: number,
        public year: number,
        public otherMonth: boolean,
    ) {}
}

@Injectable({
    providedIn: "root",
})
export class PrimeComponentService {
    private readonly translateService = inject(TranslateService);

    private cachedCalendarSettings: { [key: string]: CalendarSettings } = {};
    private calendarSettingsSubject = new BehaviorSubject<CalendarSettings>(
        new CalendarSettings("nl-BE", "dd/mm/yy", "mm/yy", "24", 1),
    );

    constructor(
        private primeNGConfig: PrimeNG,
        private readonly translate: TranslateService,
    ) {
        this.translate.onLangChange.subscribe(() => {
            this.updateCalendarSettings();
        });
    }

    get calendarYearRange(): string {
        const curYear = new Date().getFullYear();
        return `2010:${curYear}`;
    }

    get calendarYearRangeFuture(): string {
        const curYear = new Date().getFullYear();
        return `2010:${curYear + 10}`;
    }

    calendarYearRangeFutureAmountOfYears(numberOfYears: number): string {
        const curYear = new Date().getFullYear();
        return `2010:${curYear + numberOfYears}`;
    }

    calendarFooterConfiguration(): LegendRow[] {
        return [
            { color: "#26990f", translationKey: "calendar.MeasuredCorrectly" },
            { color: "#fec32d", translationKey: "calendar.notRepresentative" },
            { color: "#fb5555", translationKey: "calendar.excluded" },
            { color: "#453d55", translationKey: "calendar.predicted" },
        ];
    }

    //#region Dropdown

    createDropdownList<T>(
        dataList: T[],
        getData: (data: T) => any,
        getLabel: (data: T) => string,
        includeEmpty = true,
        emptyLabel = "form.empty",
        getStyleClass: (data: T) => string = null,
    ): SelectItem[] {
        const selectItems = new Array<SelectItem>();
        if (!dataList) return selectItems;

        if (includeEmpty) {
            const emptyOption = this.createEmptyOption(emptyLabel);
            selectItems.push(emptyOption);
        }

        for (const data of dataList) {
            const selectItem = this.createDropdownItem(data, getData, getLabel, getStyleClass);

            selectItems.push(selectItem);
        }

        return selectItems;
    }

    createDropdownItem<T>(
        data: T,
        getData: (data: T) => any,
        getLabel: (data: T) => string,
        getStyleClass: (data: T) => string = null,
    ): SelectItem {
        const selectItem = {
            label: getLabel(data),
            value: getData(data),
        } as SelectItem;

        if (getStyleClass) {
            selectItem.styleClass = getStyleClass(data);
        }

        return selectItem;
    }

    createEmptyOption(label: string, emptyOptionValue = null): SelectItem {
        const emptyOption = { label: "", value: emptyOptionValue } as SelectItem;

        if (label && label !== "") {
            this.setEmptyOptionLabel(emptyOption, label);

            this.translate.onLangChange.subscribe(() => {
                this.setEmptyOptionLabel(emptyOption, label);
            });
        }

        return emptyOption;
    }

    private setEmptyOptionLabel(emptyOption: SelectItem, label: string): string | void {
        if (!label || label === "") return label;

        this.translate.get(label).subscribe((emptyLabel) => {
            emptyOption.label = emptyLabel;
        });
    }

    //#endregion Dropdown

    //#region Calendar Localization

    calendarSettings() {
        return this.calendarSettingsSubject;
    }

    get locale(): string {
        const currentLang = this.translate.currentLang;
        const calendarSettings = this.cachedCalendarSettings[currentLang];
        if (!calendarSettings) return null;

        return calendarSettings.locale;
    }

    private updateCalendarSettings() {
        const currentLang = this.translate.currentLang;

        this.translate.get("calendar").subscribe((translations: { [key: string]: any }) => {
            const useNlFormat = currentLang === "nl" || currentLang === "de";

            const firstDayOfWeekIsMonday = useNlFormat;

            const locale = useNlFormat ? "nl-BE" : "en-GB";
            const dateFormat = "dd/mm/yy";
            const hourFormat = "24";
            const firstDayOfWeek = firstDayOfWeekIsMonday ? 1 : 0;
            const monthFormat = "mm/yy";

            const calendarSettings = new CalendarSettings(locale, dateFormat, monthFormat, hourFormat, firstDayOfWeek);

            this.cachedCalendarSettings[currentLang] = calendarSettings;

            this.calendarSettingsSubject.next(calendarSettings);

            translations = JsonUtils.deepClone(translations);
            for (const translationListKey of [
                "dayNames",
                "dayNamesShort",
                "dayNamesMin",
                "monthNames",
                "monthNamesShort",
            ]) {
                let orderedValues: { [p: string]: string };
                if (["dayNames", "dayNamesShort", "dayNamesMin"].contains(translationListKey)) {
                    orderedValues = this._orderDayNames(translations[translationListKey]);
                } else {
                    orderedValues = this._orderMonthNames(translations[translationListKey]);
                }

                translations[translationListKey] = Object.values(orderedValues);
            }

            this.primeNGConfig.setTranslation(translations);
        });
    }

    /**
     * Values from translation are alphabetical, make sure they are in the correct order.
     * PrimeNG always indexes dayNames/dayNamesShort/dayNamesMin by Date.getDay() (0 = Sunday),
     * independent of firstDayOfWeek (which only rotates which column the grid starts on),
     * so the array must always be Sunday-first.
     * @param value
     * @private
     */
    private _orderDayNames(value: { [p: string]: string }) {
        const correctOrder = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
        const orderedValues: { [p: string]: string } = {};
        for (const dayName of correctOrder) {
            orderedValues[dayName] = value[dayName];
        }
        return orderedValues;
    }

    /**
     * Values from translation are alphabetical, make sure they are in the correct order
     * @param value
     * @private
     */
    private _orderMonthNames(value: { [p: string]: string }) {
        const correctOrder = [
            "january",
            "february",
            "march",
            "april",
            "may",
            "june",
            "july",
            "august",
            "september",
            "october",
            "november",
            "december",
        ];
        const orderedValues: { [p: string]: string } = {};
        for (const monthName of correctOrder) {
            orderedValues[monthName] = value[monthName];
        }
        return orderedValues;
    }

    //#endregion Calendar Localization

    createTimeAggregationOptions(supportedTimeAggregations: ViewModelEnum[]): TwoLevelSelectOption[] {
        const lastHours = [1, 2, 3, 6, 12];
        const lastDays = [1, 3, 7];

        const configuration = [] as TwoLevelSelectOption[];

        for (const hour of lastHours) {
            configuration.push({
                value: hour,
                label:
                    hour === 1
                        ? this.translateService.instant("liveTiles.measuredData.lastHour")
                        : this.translateService.instant("liveTiles.measuredData.lastHours", { hour: hour }),
            } as TwoLevelSelectOption);
        }

        for (const day of lastDays) {
            configuration.push({
                value: day * 24, // in hours
                label:
                    day === 1
                        ? this.translateService.instant("liveTiles.measuredData.lastDay")
                        : this.translateService.instant("liveTiles.measuredData.lastDays", { day: day }),
            } as TwoLevelSelectOption);
        }

        for (const rangeSelectItem of configuration) {
            rangeSelectItem.relations = [];

            if (rangeSelectItem.value >= 24) {
                // all aggregation types available when we're taking data range including multiple days
                rangeSelectItem.relations.push(...supportedTimeAggregations);
            } else {
                if (rangeSelectItem.value > 1) {
                    // day aggregation type not available when we're taking data range <= 1day
                    rangeSelectItem.relations.push(...supportedTimeAggregations.filter((x) => x.value !== "day"));
                } else {
                    // day aggregation type and hour aggregation type not available when we're taking data range <= 1hour
                    rangeSelectItem.relations.push(
                        ...supportedTimeAggregations.filter((x) => x.value !== "day" && x.value !== "hour"),
                    );
                }
            }
        }

        return configuration;
    }

    getChartTimeAxeUnit(timeAggregation: TimeAggregationType): TimeUnit {
        return timeAggregation === "day" ? "day" : timeAggregation === "hour" ? "hour" : "minute";
    }
}

export class DateFormControl extends SigncoFormControl {
    selectionMode: "single" | "multiple" | "range" | undefined = "multiple";
    private shiftDown: boolean;
    private shiftDate: DateMeta;

    constructor(
        public calendar?: DatePicker,
        formState?: any,
        validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
        asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
    ) {
        super(formState, validatorOrOpts, asyncValidator);
        this.setCalendar(calendar);
    }

    setCalendar(calendar: DatePicker) {
        if (this.calendar === calendar) return;

        if (calendar) {
            calendar.selectionMode = this.calendar ? this.calendar.selectionMode : this.selectionMode;
        }

        setTimeout(() => {
            this.calendar = calendar;

            if (this.calendar) {
                const originalOnDateSelect = this.calendar.onDateSelect;
                this.calendar.onDateSelect = (event: MouseEvent, dateMeta: DateMeta) => {
                    if (!this.calendar) return;

                    this.shiftDown = event.shiftKey;

                    // When deselecting the only selected month from the month view,
                    // the original function would set the value to an empty array.
                    // When that happens other functions like updateUI would throw an error because
                    // there is no check if the array is empty.
                    if (
                        this.calendar.view === "month" &&
                        this.calendar.isMultipleSelection() &&
                        this.calendar.isSelected(dateMeta) &&
                        this.value &&
                        this.value.length <= 1
                    ) {
                        this.calendar.inputFieldValue = undefined;
                        this.calendar.updateModel(undefined);
                        this.calendar.updateInputfield();
                        return;
                    }

                    if (!this.shiftDate || !this.shiftDown || !this.calendar.isMultipleSelection()) {
                        this.shiftDate = dateMeta;
                        originalOnDateSelect.call(this.calendar, event, dateMeta);
                        return;
                    }

                    const fromDate = this.dateMetaToDate(this.shiftDate);
                    const toDate = this.dateMetaToDate(dateMeta);

                    const lowestDate = fromDate < toDate ? fromDate : toDate;
                    const highestDate = lowestDate === fromDate ? toDate : fromDate;

                    const curMonth = lowestDate.getMonth();

                    const curDates = this.getValueAsMultiple();

                    for (let date = lowestDate; date <= highestDate; date = date.addDays(1)) {
                        if (curDates && !!curDates.find((x) => x.getTime() === date.getTime())) continue;

                        const meta = new DateMeta(
                            date.getDate(),
                            date.getMonth(),
                            date.getFullYear(),
                            date.getMonth() !== curMonth,
                        );

                        if (this.calendar.shouldSelectDate(meta)) {
                            this.calendar.selectDate(meta);
                        }
                    }

                    this.calendar.updateInputfield();

                    // this.shiftDate = null;
                };

                const originalUpdateInputfield = this.calendar.updateInputfield;
                this.calendar.updateInputfield = () => {
                    if (!this.calendar) return;

                    if (this.calendar.selectionMode === "multiple") {
                        // Check if the date is a perfect range (from-to with all dates inbetwene)
                        // If so we print it as [from] - [to]

                        const range = this.getValueAsRange();
                        if (!range || !range.to || !range.to.getTime || !range.from || !range.from.getTime) {
                            originalUpdateInputfield.call(this.calendar);
                            return;
                        }

                        if (Array.isArray(this.calendar.value)) {
                            const selectedDates = this.calendar.value as Date[];

                            // Take the difference between the dates and divide by milliseconds per day.
                            const datesBetweenFromTo = Math.round(
                                (range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24),
                            );

                            // Do this only if
                            // 1) from !== to
                            // 2) calendar.value as Date[]'s length === amount of days between from - to + 1
                            if (
                                range.from.getTime() !== range.to.getTime() &&
                                selectedDates.length === datesBetweenFromTo + 1
                            ) {
                                const formattedValue =
                                    this.calendar.formatDateTime(range.from) +
                                    " - " +
                                    this.calendar.formatDateTime(range.to);

                                this.calendar.inputFieldValue = formattedValue;
                                if (
                                    this.calendar.inputfieldViewChild &&
                                    this.calendar.inputfieldViewChild.nativeElement
                                ) {
                                    this.calendar.inputfieldViewChild.nativeElement.value =
                                        this.calendar.inputFieldValue;
                                }

                                return;
                            }
                        }
                    }

                    originalUpdateInputfield.call(this.calendar);
                };

                const originalIsMonthSelected = this.calendar.isMonthSelected;
                const compareMonth = (date: Date, month: number, year: number) =>
                    date.getFullYear() === year && date.getMonth() === month;

                this.calendar.isMonthSelected = (month) => {
                    if (!this.calendar) {
                        return;
                    }

                    // When checking if a month is selected in a month view no need to compare day.
                    if (this.calendar.view === "month") {
                        if (!this.calendar.isMultipleSelection()) {
                            return this.value && compareMonth(this.value, month, this.calendar.currentYear);
                        }

                        return (
                            this.value &&
                            this.value.some((v: Date) => compareMonth(v, month, this.calendar.currentYear))
                        );
                    }

                    return originalIsMonthSelected.call(this.calendar, month);
                };

                this.calendar.updateInputfield();
            }
        });
    }

    dateMetaToDate(dateMeta: DateMeta): Date {
        return new Date(dateMeta.year, dateMeta.month, dateMeta.day);
    }

    toggleDateSelectionMode() {
        this.setDateSelectionMode(this.selectionMode === "range" ? "multiple" : "range");
    }

    setDateSelectionMode(selectionMode: "single" | "multiple" | "range" | undefined) {
        this.selectionMode = selectionMode;

        if (!this.calendar || this.calendar.selectionMode === selectionMode) return;

        this.calendar.selectionMode = selectionMode;

        this.updateInput();
    }

    private updateInput() {
        let dates: Date[] = null;

        if (this.selectionMode === "range") {
            if (Array.isArray(this.value) && this.value.length > 0) {
                dates = new Array<Date>();

                const datesAsTime = this.value.map((x) => (x as Date).getTime());
                // eslint-disable-next-line prefer-spread
                const minDate = new Date(Math.min.apply(Math, datesAsTime));
                // eslint-disable-next-line prefer-spread
                let maxDate = new Date(Math.max.apply(Math, datesAsTime));

                if (minDate === maxDate) {
                    maxDate = new Date(maxDate);
                }

                dates.push(minDate);
                dates.push(maxDate);
            }
        }

        if (this.selectionMode === "multiple") {
            if (Array.isArray(this.value)) {
                const from = this.value.length > 0 ? (this.value[0] as Date) : null;

                if (from) {
                    dates = new Array<Date>();
                    const to = this.value.length === 2 ? (this.value[1] as Date) || new Date(from) : new Date(from);

                    for (let date = from; date <= to; date = date.addDays(1)) {
                        dates.push(new Date(date));
                    }
                }
            }
        }

        this.patchValue(dates);

        // https://github.com/primefaces/primeng/issues/5933
        if (this.calendar) {
            setTimeout(() => {
                this.calendar.updateInputfield();
            });
        }
    }

    getValueAsRange(): { from: Date; to: Date } {
        if (!this.value) return null;

        let from: Date;
        let to: Date;

        if (this.selectionMode === "range") {
            from = this.value[0] as Date;
            to = this.value[1] as Date;
        }

        if (this.selectionMode === "single") {
            from = this.value as Date;
            to = this.value as Date;
        }

        if (this.selectionMode === "multiple") {
            const sortedDates = (this.value as Date | Date[]).toList<Date>().sortBy((x) => x.getTime());
            from = sortedDates.takeFirstOrDefault();
            to = sortedDates.takeLastOrDefault();
        }

        if (from === to) {
            to = new Date(to);
        }

        return {
            from: from,
            to: to || new Date(from),
        };
    }

    getValueAsMultiple(): Date[] {
        if (!this.value) return null;

        let dates: Date[];

        if (this.calendar.selectionMode === "multiple") dates = this.value as Date[];
        if (this.calendar.selectionMode === "single") dates = [this.value as Date];

        if (this.calendar.selectionMode === "range") {
            const range = this.getValueAsRange();

            dates = new Array<Date>();
            for (let date = range.from; date <= range.to; date = date.addDays(1)) {
                dates.push(new Date(date));
            }
        }

        return dates;
    }
}
